import { chakra } from '@chakra-ui/react';
import { forwardRef, type ReactNode } from 'react';

type SwitchSize = 'sm' | 'md';
const dims: Record<SwitchSize, { w: string; h: string; thumb: string; travel: string }> = {
  sm: { w: '7', h: '4', thumb: '2.5', travel: '12px' },
  md: { w: '9', h: '5', thumb: '3.5', travel: '16px' },
};

const Root = chakra('label', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '2.5',
    fontFamily: 'body',
    fontSize: 'md',
    color: 'text.primary',
    cursor: 'pointer',
    userSelect: 'none',
    '&:has(input:disabled)': { cursor: 'not-allowed', color: 'text.muted', opacity: 0.7 },
    '& input:checked + span': { transform: 'translateX(var(--switch-travel))' },
  },
});

const Track = chakra('input', {
  base: {
    appearance: 'none',
    margin: 0,
    borderRadius: 'full',
    bg: 'border.strong',
    cursor: 'inherit',
    transitionProperty: 'background-color, box-shadow',
    transitionDuration: 'fast',
    transitionTimingFunction: 'standard',
    outline: 'none',
    _checked: { bg: 'action.primary' },
    _focusVisible: { boxShadow: 'focus' },
  },
});

export interface SwitchProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'type' | 'color'> {
  size?: SwitchSize;
  label?: ReactNode;
}

/** An on/off toggle (native checkbox with role="switch" for correct a11y). */
export const Switch = forwardRef<HTMLInputElement, SwitchProps>(function Switch(
  { size = 'md', label, disabled, ...rest },
  ref,
) {
  const d = dims[size];
  return (
    <Root style={{ ['--switch-travel' as string]: d.travel }}>
      <chakra.span position="relative" display="inline-flex" alignItems="center" flexShrink={0}>
        <Track ref={ref} type="checkbox" role="switch" disabled={disabled} w={d.w} h={d.h} {...rest} />
        <chakra.span
          aria-hidden
          position="absolute"
          left="2px"
          boxSize={d.thumb}
          borderRadius="full"
          bg="surface.default"
          boxShadow="xs"
          pointerEvents="none"
          transitionProperty="transform"
          transitionDuration="fast"
          transitionTimingFunction="standard"
        />
      </chakra.span>
      {label != null && <span>{label}</span>}
    </Root>
  );
});
